import { createContext, Dispatch, ReactNode, SetStateAction, useMemo, useState } from 'react';

import { Locale } from '../../../core/i18n/Locale';
import { logger } from '../../../core/Log/logger';
import { useCurrentSite } from '../../../shared/providers/currentSite/useCurrentSite';

type WemapContextValue = {
    emmid: number;
    openedPinpointId: number | null;
    setOpenedPinpointId: Dispatch<SetStateAction<number | null>>;
    isDrawerExpanded: boolean;
    setIsDrawerExpanded: Dispatch<SetStateAction<boolean>>;
};

export const WemapContext = createContext<WemapContextValue | undefined>(undefined);

type WemapProviderProps = {
    children: ReactNode;
    initialOpenedPinpointId?: WemapContextValue['openedPinpointId'];
    initialIsDrawerExpanded?: WemapContextValue['isDrawerExpanded'];
};

const emmidByLanguage: Record<Locale, number> = {
    'fr-FR': 30750,
    'de-DE': 31918,
    'de-CH': 31932,
    'nl-NL': 31930,
    'en-US': 31921,
    'en-GB': 31933,
    'en-CA': 31934,
    'it-IT': 31917,
    'es-ES': 31916,
};

export function WemapProvider({
    children,
    initialOpenedPinpointId = null,
    initialIsDrawerExpanded = false,
}: WemapProviderProps) {
    const { currentSite } = useCurrentSite();
    const { locale } = currentSite;

    const siteEmmid = emmidByLanguage[locale];
    if (!siteEmmid) {
        logger.warning(`Wemap missing a Map ID for locale "${locale}"`, { locale });
    }

    const emmid = siteEmmid ?? emmidByLanguage['en-US'];

    const [openedPinpointId, setOpenedPinpointId] =
        useState<WemapContextValue['openedPinpointId']>(initialOpenedPinpointId);

    const [isDrawerExpanded, setIsDrawerExpanded] =
        useState<WemapContextValue['isDrawerExpanded']>(initialIsDrawerExpanded);

    const context = useMemo(
        () => ({
            emmid,
            openedPinpointId,
            setOpenedPinpointId,
            isDrawerExpanded,
            setIsDrawerExpanded,
        }),
        [emmid, openedPinpointId, isDrawerExpanded],
    );

    return <WemapContext.Provider value={context}>{children}</WemapContext.Provider>;
}
